import { AdminButton } from '@website/components/admin/AdminButton';
import Link from 'next/link';
import { Badge, Button, Card } from 'react-bootstrap';
import { FaFileInvoice } from 'react-icons/fa';
import { MdHelp } from 'react-icons/md';
import Imgix from 'react-imgix';
import { getImageUrl } from '../../media/utils/imgix';

type Props = {
  image: { key: string | null };
  title: string;
  text?: string;
  url?: string;
  subText1?: string;
  link?: string;
  active?: boolean;
  badgeText?: string;
  leaveReviewLink?: string;
  linkToReview?: string;
  reviewLinkText?: string;
  isTripDeparted: boolean;
};

export default function BookingTripCardSmaller({
  title,
  text,
  image,
  url,
  subText1,
  badgeText,
  link,
  active,
  leaveReviewLink,
  linkToReview,
  isTripDeparted,
  reviewLinkText = 'Leave a review for the trip',
}: Props) {
  // Set the image saturation
  const saturation = active ? {} : { sat: '-100' }; // Grayscale if inactive

  // eslint-disable-next-line no-param-reassign
  link = url ? `/trips/${url}` : link;

  return (
    <Card
      className="flex flex-row"
      style={{
        height: '200px',
      }}
    >
      <Link href={link} passHref className="text-decoration-none">
        <Imgix
          src={`${getImageUrl(image.key)}`}
          imgixParams={{
            auto: 'compress,format,enhance,redeye',
            crop: 'faces,entropy',
            fit: 'crop',
            ar: `16:10`, // Aspect ratio
            ...saturation,
          }}
          htmlAttributes={{
            alt: title,
          }}
          className="rounded-start h-100"
          sizes="(min-width: 1400px) 420px, (min-width: 1200px) 360px, (min-width: 992px) 299px, (min-width: 768px) 220px, (min-width: 576px) 208px, 100vw"
        />
      </Link>
      <Card.Body>
        <Card.Title>
          <Link href={link} passHref className="text-dark">
            <p className="h5 fw-bold">{title}</p>
          </Link>
        </Card.Title>

        <Card.Text>
          <span className="h6">{text}</span>
          <span className="flex flex-row h6 fw-bold d-block justify-content-center">
            {subText1}{' '}
            {badgeText && (
              <>
                <Badge bg="warning">{badgeText}</Badge>
              </>
            )}
          </span>
        </Card.Text>
        <div className="d-grid gap-2">
          <Link href={link} passHref legacyBehavior>
            <Button
              variant={isTripDeparted ? 'light' : 'primary'}
              size="sm"
              as="a"
            >
              <label className="me-2">
                <FaFileInvoice size="1.4em" />
              </label>
              View Booking
            </Button>
          </Link>
          {!isTripDeparted && (
            <Link href="/help/contact" passHref legacyBehavior>
              <Button as="a" size="sm">
                <label className="me-2">
                  <MdHelp size="1.4em" />
                </label>
                Get help with your Trip
              </Button>
            </Link>
          )}
          {leaveReviewLink && isTripDeparted && (
            <Link href={leaveReviewLink} passHref legacyBehavior>
              <Button as="a" size="sm">
                {reviewLinkText}
              </Button>
            </Link>
          )}

          {linkToReview && isTripDeparted && (
            <Link href={linkToReview} passHref legacyBehavior>
              <AdminButton as="a" size="sm">
                View review
              </AdminButton>
            </Link>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
